// @h/viewer/usePublicTour.ts
import * as TourApi from "@a/tours";
import { useAuth } from "@h/useCtx";
import { isEmbedOriginAllowed } from "@u/embed";
import { getEmbedderOrigin, isInIframe } from "@u/iframe";
import { setTourGrant } from "@/lib/tourGrant";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

export const usePublicTour = (shortId?: string) => {
  const { user } = useAuth();
  const accessKey = `tour_access_${shortId}`;

  const [hasAccess, setHasAccess] = useState(() => {
    return sessionStorage.getItem(accessKey) === "true";
  });

  const {
    data: tour,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["public-tour", shortId],
    queryFn: () => TourApi.fetchTourByShortId(shortId!),
    enabled: !!shortId,
    staleTime: 1000 * 60 * 5,
    retry: (count, err: unknown) => {
      const error = err as { code?: string };
      return error?.code === "PGRST116" ? false : count < 1;
    },
  });

  // 1. Verificamos si el tour existe y si el usuario actual es el dueño
  const isOwner = !!tour && !!user && tour.user_id === user.id;
  const isActive = tour?.state === "published";

  // 2. Definimos si el tour es accesible (Por ser dueño o por estar publicado)
  const isAccessible = isOwner || isActive;

  // 3. Lógica de bloqueo (Si es accesible pero requiere password y no la tiene)
  // El dueño nunca ve el tour bloqueado
  const isLocked =
    isAccessible && !!tour?.requires_password && !hasAccess && !isOwner;

  // 4. Banderas de estado final para la UI
  const notFound = !!error || (!isLoading && !isAccessible);
  const filteredTour = isAccessible ? tour : null;

  // 5. Bloqueo de embed (refuerzo cliente del frame-ancestors del middleware).
  //    Whitelist vacío = embed en cualquier lado. El dueño nunca se bloquea.
  //    Mismo origen (preview del editor) siempre permitido.
  const isEmbedBlocked = useMemo(() => {
    if (!filteredTour || isOwner) return false;
    if (!isInIframe()) return false;
    const list = filteredTour.settings?.embed_domains ?? [];
    if (list.length === 0) return false;
    const embedder = getEmbedderOrigin();
    if (embedder && embedder === window.location.origin) return false;
    if (!embedder) return true; // dentro de iframe, restringido, origen desconocido → bloquear
    return !isEmbedOriginAllowed(embedder, list);
  }, [filteredTour, isOwner]);

  // Verifica contra el servidor — nunca compara en el cliente. Un password
  // correcto acuña un grant token server-side; el viewer lee el scene graph del
  // tour protegido con ese token (tour_view_graph), ya que la RLS ahora oculta
  // los tours con password del acceso anónimo.
  const checkPassword = useCallback(
    async (input: string): Promise<boolean> => {
      if (!tour) return false;
      const token = await TourApi.unlockTour(tour.id, input);
      if (!token) return false;
      setTourGrant(tour.id, token);
      sessionStorage.setItem(accessKey, "true"); // ← persistir el flag de UI
      setHasAccess(true);
      return true;
    },
    [tour, accessKey],
  );

  return {
    tour: filteredTour,
    loading: isLoading,
    notFound,
    isLocked,
    isEmbedBlocked,
    checkPassword,
  };
};
